import React from 'react';
import { Box, Checkbox } from '@material-ui/core';
import Autocomplete, {
  AutocompleteClassKey,
} from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { createStyles, makeStyles } from '@material-ui/styles';
import { UserAvatar } from 'src/components/User';
import { ListboxComponent } from 'src/components/Select/ListBox';
import { BaseTextField, TextFieldSizeVariant } from 'src/components/TextField';
import { ChevronDownIcon, CheckIcon } from 'src/components/Icons';
import { GraySmall, NonHoverBorder } from 'src/theme/colors';
import { BaseChip } from 'src/components/UI';
import BaseTypography from 'src/components/Text/BaseTypography';

import { DropdownMenuShadow } from 'src/theme/shadows';
import { AvatarSizeType } from 'src/components/User/UserAvatar';

const useStyles = makeStyles(() =>
  createStyles({
    list: {
      padding: 0,
    },
    dropdownIcon: {
      height: '15px',
      width: '15px',
      color: props => (props.disabled ? NonHoverBorder : GraySmall),
    },
    listPaper: {
      border: `1px solid ${NonHoverBorder}`,
      boxShadow: DropdownMenuShadow,
    },
    label: {
      '&.MuiTextField-root label.MuiInputLabel-outlined': {
        '&.Mui-disabled': {
          color: NonHoverBorder,
        },
      },
      '&.MuiTextField-root .MuiOutlinedInput-root.Mui-disabled': {
        color: NonHoverBorder,
      },
    },
  }),
);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// ComboBoxOption can be used for many types of multi-selects
// so it contains properties that other select can use
export type ComboBoxOption = {
  label: string;
  type: string;
  id: string;
  getstreamId?: string;
  avatar?: string;
  fallbackColor?: string;
  companyName?: string;
  style?: React.CSSProperties;
  deleteIconStyle?: React.CSSProperties;
};

export interface ChannelMemberSelectComboBoxOption extends ComboBoxOption {
  companyId?: string;
}

interface AdditionalComboBoxProps {
  openOnFocus?: boolean;
  inputProps?: InputProps;
}

interface InputProps {
  value?: string;
  autoFocus?: boolean;
  onChange?: (event: any) => void;
}

interface ComboBoxProps {
  id: string;
  label: string;
  placeholder?: string;
  options: Array<ComboBoxOption | ChannelMemberSelectComboBoxOption>;
  onChange: (event: React.ChangeEvent<{ value: any }>, value: any) => void;
  values: any[] | any;
  additionalComboBoxProps?: AdditionalComboBoxProps;
  filterOptions?: any;
  withAvatars?: boolean;
  withChips?: boolean;
  showPlaceholder?: boolean;
  hideSelectedItems?: boolean;
  multiple: boolean;
  error?: boolean;
  helperText?: any;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  textFieldVariant?: TextFieldSizeVariant;
  disabledIDs?: string[];
  autoCompleteClasses?: Partial<Record<AutocompleteClassKey, string>>;
  avatarSize?: AvatarSizeType;
  disableCloseOnSelect?: boolean;
  open?: boolean; // Controls the open state of auto-complete
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  popupIcon?: React.ReactNode;
}

const ComboBox = ({
  id,
  label,
  placeholder,
  showPlaceholder = true,
  options,
  values,
  onChange,
  onBlur,
  additionalComboBoxProps,
  filterOptions,
  withAvatars = false,
  avatarSize = 'regularLarge',
  withChips = false,
  hideSelectedItems,
  multiple,
  error,
  helperText,
  disabled = false,
  textFieldVariant = 'skinny',
  disabledIDs = [],
  autoCompleteClasses = {},
  disableCloseOnSelect = false,
  onKeyDown,
  open,
  popupIcon,
}: ComboBoxProps) => {
  const classes = useStyles({ disabled });

  const getInputProps = (): InputProps => {
    if (additionalComboBoxProps) {
      if (additionalComboBoxProps.inputProps) {
        return additionalComboBoxProps.inputProps;
      }
    }
    return {};
  };

  const getOpenProps = () => (open !== undefined ? { open } : {});

  // openOnFocus indicates whether the dropdown list should open on focus or not
  const { openOnFocus = false } = additionalComboBoxProps || {};
  const getInputValueProp = () =>
    disabled && { inputValue: values ? values.label : '' };

  const selectedOptionsRenderer = (opts: ComboBoxOption[], getTagProps: any) =>
    opts.map((option: ComboBoxOption, index: number) => (
      <BaseChip
        label={option.label}
        {...getTagProps({ index })}
        style={option.style ? option.style : {}}
        deleteIconStyle={option.deleteIconStyle || {}}
      />
    ));

  const isOptionDisabled = (option: ComboBoxOption) =>
    disabledIDs.some(disabledID => option.id.indexOf(disabledID) > -1);

  const optionItemRenderer = (option: ComboBoxOption, { selected }) => {
    const isDisabled = isOptionDisabled(option);
    if (withChips) {
      return (
        <BaseChip
          label={option.label}
          style={option.style ? option.style : {}}
        />
      );
    }
    return (
      <Box
        display="flex"
        justifyItems="space-between"
        alignItems="center"
        width={1}
      >
        {withAvatars ? (
          <Box mr={1}>
            <UserAvatar
              type="image"
              primaryTextVariant="tableMain"
              name={option.label}
              avatarUrl={option.avatar}
              fallbackColor={option.fallbackColor}
              avatarSize={avatarSize}
              initialLetters={
                option?.type === 'company' ? option.label.split(' ')[0] : ''
              }
            />
          </Box>
        ) : (
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
        )}

        <Box>
          <BaseTypography fontType="13Medium">{option.label}</BaseTypography>
          {option.companyName && (
            <BaseTypography>{option.companyName || ''}</BaseTypography>
          )}
        </Box>
        {isDisabled && (
          <Box fontSize="14px" marginLeft="auto">
            <CheckIcon color="primary" fontSize="inherit" />
          </Box>
        )}
      </Box>
    );
  };

  const inputRenderer = params => (
    <BaseTextField
      sizeVariant={textFieldVariant}
      {...params}
      label={label}
      variant="outlined"
      placeholder={showPlaceholder ? placeholder || `Choose ${label}` : ''}
      fullWidth
      {...getInputProps()}
      error={error}
      className={classes.label}
      helperText={helperText}
      disabled={disabled}
      inputProps={{
        ...params.inputProps,
        isautocomplete: 'true',
      }}
    />
  );
  return (
    <Autocomplete
      limitTags={2}
      classes={{
        listbox: classes.list,
        paper: classes.listPaper,
        ...autoCompleteClasses,
      }}
      multiple={multiple}
      id={id}
      options={options}
      value={values}
      {...getInputValueProp()}
      {...getOpenProps()}
      onBlur={onBlur}
      filterOptions={filterOptions}
      onChange={onChange}
      onKeyDown={onKeyDown}
      popupIcon={
        popupIcon !== undefined ? (
          popupIcon
        ) : (
          <ChevronDownIcon className={classes.dropdownIcon} />
        )
      }
      ListboxComponent={
        ListboxComponent as React.ComponentType<
          React.HTMLAttributes<HTMLElement>
        >
      }
      // to shrink row size for color chips list or small avatars
      ListboxProps={{
        chipsized: String(withChips || avatarSize === '18small'),
      }}
      renderTags={selectedOptionsRenderer}
      getOptionLabel={option => option.label}
      getOptionSelected={(option: ComboBoxOption) =>
        multiple
          ? values.find((item: any) => item.id === option.id)
          : values.id === option.id
      }
      renderOption={optionItemRenderer}
      renderInput={inputRenderer}
      openOnFocus={openOnFocus}
      disableClearable={multiple}
      filterSelectedOptions={hideSelectedItems}
      disabled={disabled}
      getOptionDisabled={option => isOptionDisabled(option)}
      disableCloseOnSelect={disableCloseOnSelect}
    />
  );
};

export default ComboBox;
